import React, { Fragment, useEffect, useState } from "react";
import { Box, Divider, Grid, Switch, Typography, useTheme } from "@mui/material";
import CommonButton from "../../common/components/CommonButton";
import CommonDialog from "../../common/components/CommonDialog";
import CommonFormField from "../../common/components/CommonFormField";
import CommonSelect from "../../common/components/CommonSelect";
import { EventCateory, EventType, Reminders, removeEmptyFields, Repeats } from "../../common/const";
import CustomTimePicker from "../../common/components/CustomTimePicker";
import { Close } from "@mui/icons-material";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import { setLoading } from "../../features/authSlice";
import { useDispatch, useSelector } from "react-redux";
import { createCalenderEvent, updateCalenderEvent } from "../../api/calendarApi";
import moment from "moment";
import { getWishListCollege } from "../../api/collegeApi";

const CreateEventDialog = ({
    eventOpen,
    setEventOpen,
    isEdit,
    getEvents,
    getAllEvents,
    selectedEvent,
    setSelectedEvent,
    setViewOpen
}) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const [errors, setErrors] = useState({});
    const [colleges, setWishListCollege] = useState([]);
    const [formData, setFormData] = useState({
        title: '',
        category: '',
        linkcollege: '',
        description: '',
        eventdate: '',
        eventtime: '',
        reminder: '',
        repeat: Repeats[0].value,
        priority: 'HIGH',
        synccalendar: false
    });

    const validateField = (name, value) => {
        switch (name) {
            case 'title':
                if (!value) return `Event Title is required`;
                return '';
            case 'category':
                if (!value) return `Category is required`;
                return '';
            case 'eventdate':
                if (!value) return `Date is required`;
                return '';
            case 'eventtime':
                if (!value) return 'Time is required';
                return '';
            default:
                return '';
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        // Clear error for the field being changed
        if (errors[name]) {
            const error = validateField(name, value);
            if (!error) {
                setErrors(prev => ({ ...prev, [name]: undefined }));
            } else {
                setErrors(prev => ({ ...prev, [name]: error }));
            }
        }
    };

    const validateForm = () => {
        const newErrors = {};
        Object.keys(formData).forEach(key => {
            const error = validateField(key, formData[key]);
            if (error) newErrors[key] = error;
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        try {
            if (!validateForm()) return;
            const data = removeEmptyFields({
                "user_id": user?.user_id,
                "event_title": formData.title,
                "event_category": formData.category,
                "description": formData.description,
                "event_start_timestamp": new Date(`${formData.eventdate}T${formData.eventtime}:00`).toISOString(),
                "reminder": formData.reminder !== '' ? formData.reminder : 'NONE',
                "type": EventType[1],
                "repeat": formData.repeat,
                "priority": formData.priority,
                "student_id": user?.student_id,
                "syncExternal": formData.synccalendar,
                "college_id": formData.linkcollege,
                "is_active": true
            });
            let response;

            if (selectedEvent?.event_id) {
                response = await updateCalenderEvent(selectedEvent.event_id, data);
            } else {
                response = await createCalenderEvent(data);
            }
            if (selectedEvent?.event_id && response.data?.event_id) {
                showNotification('Calendar event updated successfully', 'success');
            } else if (response.data?.event_id) {
                showNotification('Calendar event created successfully', 'success');
            }
            dispatch(setLoading({
                loading: false
            }));
            getEvents();
            if (getAllEvents) {
                getAllEvents();
            }
            if (setViewOpen) {
                setViewOpen(false);
            }
            setEventOpen(false);
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    useEffect(() => {
        if (isEdit && selectedEvent) {
            setFormData({
                title: selectedEvent?.event_title,
                category: selectedEvent?.event_category,
                linkcollege: selectedEvent?.college_id,
                description: selectedEvent?.description,
                eventdate: selectedEvent?.event_start_timestamp ? moment.utc(selectedEvent?.event_start_timestamp).local().format('YYYY-MM-DD') : '',
                eventtime: selectedEvent?.event_start_timestamp ? moment.utc(selectedEvent?.event_start_timestamp).local().format('HH:mm') : '',
                reminder: selectedEvent?.reminder,
                repeat: selectedEvent?.repeat,
                priority: selectedEvent?.priority,
                synccalendar: false
            });
        }
    }, [selectedEvent]);


    const getAllWishListColleges = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getWishListCollege(user?.student_id);
            if (response.data.length > 0) {
                setWishListCollege(response.data);
            } else {
                setWishListCollege([]);
            }
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            setWishListCollege([]);
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    useEffect(() => {
        if (user?.student_id) {
            getAllWishListColleges();
        }
    }, [user?.user_id]);


    const getMinDate = () => {
        const date = new Date();
        date.setFullYear(date.getFullYear());
        return date.toISOString().split('T')[0];
    };

    return (
        <CommonDialog
            open={eventOpen}
            size="xs"
            title={
                <Fragment>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Box>
                            <Typography sx={{ fontWeight: 600, fontSize: 18 }}>{isEdit ? 'Edit Event' : 'Add New Event'}</Typography>
                            <Typography fontSize={13} color={theme.palette.background.dark}>{isEdit ? 'Edit calendar event or task' : 'Create a new calendar event or task'}</Typography>
                        </Box>
                        <Close
                            sx={{
                                color: theme.palette.background.dark, fontSize: 20,
                                float: 'right',
                                cursor: 'pointer'
                            }}
                            onClick={() => {
                                setEventOpen(false);
                                if (setSelectedEvent && isEdit) {
                                    setSelectedEvent(null);
                                }
                            }}
                        />
                    </Box>
                    <Divider />
                </Fragment>
            }
            content={
                <Grid container mt={0.2} mb={2} spacing={1.6}>
                    <Grid item xs={12} md={12}>
                        <CommonFormField
                            label="Event Title *"
                            type="email"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            error={errors.title}
                            placeholder='e.g., Submit StanFord Application'
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <CommonSelect
                            label="Category *"
                            name="category"
                            value={formData.category}
                            onChange={handleInputChange}
                            placeholder="Select Category"
                            error={errors.category}
                            options={EventCateory}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <CommonSelect
                            label="Link to College (Optional)"
                            name="linkcollege"
                            value={formData.linkcollege}
                            onChange={handleInputChange}
                            placeholder="Select College"
                            options={colleges.map((i) => {
                                return {
                                    label: i?.college?.college_name,
                                    value: i.college_id
                                }
                            })}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <CommonFormField
                            label="Description (Optional)"
                            type="email"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            placeholder='Add notes or details about the event'
                            multiline
                            minRows={3}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <CommonFormField
                            label="Date *"
                            type="date"
                            name="eventdate"
                            value={formData.eventdate}
                            onChange={handleInputChange}
                            placeholder="Date"
                            error={errors.eventdate}
                            minDate={getMinDate()}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <CustomTimePicker
                            label="Time *"
                            value={formData.eventtime}
                            onChange={handleInputChange}
                            name="eventtime"
                            placeholder="Time"
                            format="24h"
                            error={errors.eventtime}
                        />
                    </Grid>
                    <Grid item xs={6} md={6}>
                        <CommonSelect
                            label="Reminder Settings"
                            name="reminder"
                            value={formData.reminder}
                            onChange={handleInputChange}
                            placeholder="Select Reminder"
                            options={Reminders}
                        />
                    </Grid>
                    <Grid item xs={6} md={6}>
                        <CommonSelect
                            label="Repeat"
                            name="repeat"
                            value={formData.repeat}
                            onChange={handleInputChange}
                            placeholder="Select Repeat"
                            options={Repeats}
                        />
                    </Grid>
                    <Grid item md={12}>
                        <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 500, color: 'text.main', mb: 0.8 }}>
                            Set Priority
                        </Typography>
                        <Box
                            display="flex"
                            gap={1}
                        >
                            <CommonButton
                                label='High'
                                size="small"
                                height="35px"
                                marginBottom="0px"
                                fontWeight="bold"
                                background={formData.priority === 'HIGH' ? '#FFE2E2' : theme.palette.background.main}
                                color={formData.priority === 'HIGH' ? 'red' : theme.palette.background.dark}
                                handleClick={() => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        priority: 'HIGH'
                                    }))
                                }}
                                border={formData.priority !== 'HIGH' ? "1px solid lightgray" : '1px solid #FF1010'}
                            />
                            <CommonButton
                                label='Medium'
                                size="small"
                                height="35px"
                                marginBottom="0px"
                                fontWeight="bold"
                                background={formData.priority === 'MEDIUM' ? '#FEF9C2' : theme.palette.background.main}
                                color={formData.priority === 'MEDIUM' ? '#894B00' : theme.palette.background.dark}
                                handleClick={() => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        priority: 'MEDIUM'
                                    }))
                                }}
                                border={formData.priority !== 'MEDIUM' ? "1px solid lightgray" : '1px solid #894B00'}
                            />
                            <CommonButton
                                label="Low"
                                height="35px"
                                marginBottom="0px"
                                fontWeight="bold"
                                background={formData.priority === 'LOW' ? theme.palette.success.light : theme.palette.background.main}
                                color={formData.priority === 'LOW' ? '#01875F' : theme.palette.background.dark}
                                handleClick={() => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        priority: 'LOW'
                                    }))
                                }}
                                border={formData.priority !== 'LOW' ? "1px solid lightgray" : `1px solid #01875F`}
                            />
                        </Box>
                    </Grid>
                    {/* <Grid item md={12}>
                        <Box
                            border={`1px solid ${theme.palette.background.secondary}`}
                            borderRadius={1}
                            display="flex"
                            justifyContent="space-between"
                            p="5px 10px"
                            alignItems="center"
                        >
                            <Box>
                                <Typography fontSize={14}>Sync to External Calendar</Typography>
                                <Typography fontSize={14} color={theme.palette.background.dark}>Sync is event to Google Calendar or Apple Calendar</Typography>
                            </Box>
                            <Switch
                                size="small"
                                defaultValue={formData.synccalendar}
                                onChange={(e) => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        synccalendar: e.target.checked
                                    }))
                                }} />
                        </Box>
                    </Grid> */}
                </Grid>
            }
            actionButtons={(
                <CommonButton
                    label={isEdit ? 'Update' : 'Create'}
                    size="small"
                    background={theme.palette.secondary.main}
                    color={theme.palette.background.main}
                    fontWeight={700}
                    marginBottom="10px"
                    handleClick={() => {
                        handleSubmit();
                    }}
                />
            )}
        />
    )
}

export default CreateEventDialog;